import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Card, Button, Spinner, Alert, Badge, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import InviteFriends from './InviteFriends';

function FollowersList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('followers');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [publicUsers, setPublicUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    if (activeTab === 'followers') {
      fetchFollowers();
    } else if (activeTab === 'following') {
      fetchFollowing();
    } else if (activeTab === 'requests') {
      fetchPendingRequests();
    } else if (activeTab === 'discover') {
      fetchPublicUsers();
    }
  }, [activeTab]);

  // Debounce search query
  useEffect(() => {
    if (activeTab !== 'discover') return;

    const timer = setTimeout(() => {
      fetchPublicUsers();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchQuery, activeTab]);

  const fetchFollowers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/followers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch followers');
      }

      const data = await response.json();
      setFollowers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowing = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/following`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch following');
      }

      const data = await response.json();
      setFollowing(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pending requests');
      }

      const data = await response.json();
      setPendingRequests(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicUsers = async () => {
    setLoading(true);
    setError('');
    try {
      let url;
      if (searchQuery && searchQuery.length >= 2) {
        // Use search endpoint if query exists
        url = `${API_BASE_URL}/users/social/search?q=${encodeURIComponent(searchQuery)}`;
      } else {
        // Use public users endpoint for discovery
        url = `${API_BASE_URL}/users/social/public-users`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setPublicUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUser = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to follow user');
      }

      setSuccess('Follow request sent!');
      // Refresh public users list to update follow status
      fetchPublicUsers();
      fetchFollowing();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAcceptRequest = async (followerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow-requests/${followerId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to accept request');
      }

      setSuccess('Follow request accepted!');
      // Remove from pending and refresh
      setPendingRequests(prev => prev.filter(r => r.follower_id !== followerId));
      fetchFollowers(); // Refresh followers list
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRejectRequest = async (followerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow-requests/${followerId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to reject request');
      }

      setSuccess('Follow request rejected');
      // Remove from pending
      setPendingRequests(prev => prev.filter(r => r.follower_id !== followerId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/follow/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to unfollow');
      }

      setSuccess('Unfollowed successfully');
      // Remove from following list
      setFollowing(prev => prev.filter(f => f.following_id !== userId));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="followers" title={`Followers ${followers.length > 0 ? `(${followers.length})` : ''}`}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="mt-2">Loading followers...</p>
            </div>
          ) : followers.length > 0 ? (
            <div>
              {followers.map((follow) => (
                <Card key={follow.id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6
                          className="mb-0"
                          style={{ cursor: 'pointer', color: '#0d6efd' }}
                          onClick={() => navigate(`/profile/${follow.follower_id}`)}
                        >
                          {follow.follower_display_name || `User ${follow.follower_id}`}
                        </h6>
                        <small className="text-muted">
                          Following since {new Date(follow.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">No followers yet</p>
            </div>
          )}
        </Tab>

        <Tab eventKey="following" title={`Following ${following.length > 0 ? `(${following.length})` : ''}`}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="mt-2">Loading following...</p>
            </div>
          ) : following.length > 0 ? (
            <div>
              {following.map((follow) => (
                <Card key={follow.id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6
                          className="mb-0"
                          style={{ cursor: 'pointer', color: '#0d6efd' }}
                          onClick={() => navigate(`/profile/${follow.following_id}`)}
                        >
                          {follow.following_display_name || `User ${follow.following_id}`}
                        </h6>
                        <small className="text-muted">
                          Following since {new Date(follow.created_at).toLocaleDateString()}
                          {follow.status === 'pending' && (
                            <Badge bg="warning" className="ms-2">Pending</Badge>
                          )}
                        </small>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleUnfollow(follow.following_id)}
                      >
                        Unfollow
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Not following anyone yet</p>
            </div>
          )}
        </Tab>

        <Tab eventKey="requests" title={`Requests ${pendingRequests.length > 0 ? `(${pendingRequests.length})` : ''}`}>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="mt-2">Loading requests...</p>
            </div>
          ) : pendingRequests.length > 0 ? (
            <div>
              <p className="text-muted mb-3">People who want to follow you</p>
              {pendingRequests.map((follow) => (
                <Card key={follow.id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6
                          className="mb-0"
                          style={{ cursor: 'pointer', color: '#0d6efd' }}
                          onClick={() => navigate(`/profile/${follow.follower_id}`)}
                        >
                          {follow.follower_display_name || `User ${follow.follower_id}`}
                        </h6>
                        <small className="text-muted">
                          Requested {new Date(follow.created_at).toLocaleDateString()}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleAcceptRequest(follow.follower_id)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRejectRequest(follow.follower_id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">No pending follow requests</p>
            </div>
          )}
        </Tab>

        <Tab eventKey="discover" title="Discover">
          <div className="mb-3">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
            {searchQuery && searchQuery.length < 2 && (
              <Form.Text className="text-muted">
                Type at least 2 characters to search
              </Form.Text>
            )}
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="mt-2">Loading users...</p>
            </div>
          ) : publicUsers.length > 0 ? (
            <div>
              <p className="text-muted mb-3">
                {searchQuery ? `Search results for "${searchQuery}"` : 'Discover public users to follow'}
              </p>
              {publicUsers.map((user) => (
                <Card key={user.user_id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6
                          className="mb-0"
                          style={{ cursor: 'pointer', color: '#0d6efd' }}
                          onClick={() => navigate(`/profile/${user.user_id}`)}
                        >
                          {user.display_name || `User ${user.user_id}`}
                          {user.is_verified && (
                            <Badge bg="success" className="ms-2">✓ Verified</Badge>
                          )}
                        </h6>
                        {user.bio && (
                          <small className="text-muted">{user.bio}</small>
                        )}
                      </div>
                      {!user.is_following && !user.is_own_profile && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleFollowUser(user.user_id)}
                        >
                          Follow
                        </Button>
                      )}
                      {user.is_following && (
                        <Badge bg="secondary">Following</Badge>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">
                {searchQuery ? `No users found matching "${searchQuery}"` : 'No public users to discover'}
              </p>
            </div>
          )}
        </Tab>

        <Tab eventKey="invite" title="Invite a friend">
          <InviteFriends />
        </Tab>
      </Tabs>
    </div>
  );
}

export default FollowersList;
